import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';

import ChevronIcon from 'shared/components/chevron-icon';
import tinycolor from 'tinycolor2';

import { AnchorButton } from 'src/components/core/button';
import { Typography } from 'src/typography';

const brandColorReadable = css`
  color: var(
    --dcm-wallet-card-brand-text-color,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000', 20)}
  );
`;

export const CardContainer = styled.section`
  background: var(
    --dcm-wallet-card-bg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 93)}
  );
  border-radius: var(--dcm-wallet-card-border-radius, 12px);
  box-shadow: 0px 1px 2px 0px #00000008, 0px 12px 24px -8px #00000014;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
`;

export const InnerCard = styled.div`
  background: var(--dcm-wallet-card-inner-bg, ${({ theme }) => theme.colors.white});
  border-radius: var(--dcm-wallet-card-inner-border-radius, 8px);
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 100%;
`;

export const CardHeaderRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  width: 100%;
`;

export const BrandGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

export const BrandLogo = styled.img`
  border: 1px solid var(--dcm-wallet-card-logo-border, ${({ theme }) => theme.colors.grey[85]});
  border-radius: var(--dcm-wallet-card-logo-border-radius, 4px);
  height: 44px;
  object-fit: contain;
  padding: 0 4px;
  width: 44px;
`;

export const WalletLabel = styled(Typography.Label)`
  color: var(--dcm-wallet-card-label-color, ${({ theme }) => theme.colors.primaryBlack});
  letter-spacing: 1px;
`;

export const PointsBadge = styled(Link)`
  ${brandColorReadable}
  align-items: center;
  background: var(
    --dcm-wallet-card-points-badge-bg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 85)}
  );
  border-radius: var(--dcm-wallet-card-points-badge-border-radius, 12px);
  display: flex;
  gap: 4px;
  padding: 4px 10px;
  text-decoration: none;
`;

export const PointsBadgeChevron = styled(ChevronIcon)`
  fill: currentColor;
  transform: rotate(-90deg);
`;

export const BalanceLabel = styled(Typography.Label)`
  color: var(--dcm-wallet-card-balance-label-color, ${({ theme }) => theme.colors.grey[45]});
  letter-spacing: 1px;
`;

export const BalanceAmount = styled(Typography.Heading)`
  color: var(--dcm-wallet-card-balance-color, ${({ theme }) => theme.colors.primaryBlack});
  margin-bottom: 16px;
`;

const shimmer = keyframes`
  0%   { background-position: -150% 0; }
  35%  { background-position: 250% 0; }
  100% { background-position: 250% 0; }
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;

    > * {
      flex: 1;
    }
  }
`;

export const ViewActivityButton = styled(AnchorButton)`
  text-align: center;
`;

export const RewardsRow = styled(Link)`
  align-items: center;
  background: var(
    --dcm-wallet-card-rewards-bg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 75)}
  );
  border-radius: var(--dcm-wallet-card-rewards-border-radius, 8px);
  display: flex;
  gap: 12px;
  overflow: hidden;
  padding: 12px;
  position: relative;
  text-decoration: none;
  width: 100%;

  &::before {
    background: linear-gradient(
      75deg,
      transparent 0%,
      transparent 40%,
      rgba(255, 255, 255, 0.25) 50%,
      transparent 60%,
      transparent 100%
    );
    background-position: -150% 0;
    background-repeat: no-repeat;
    background-size: 200% 100%;
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
  }

  @media (prefers-reduced-motion: no-preference) {
    &::before {
      animation: ${shimmer} 8s linear infinite;
    }
  }

  > * {
    position: relative;
  }
`;

export const RewardsRowIcon = styled.span`
  align-items: center;
  border-radius: var(--dcm-wallet-card-rewards-icon-border-radius, 50%);
  border: 4px solid
    var(
      --dcm-wallet-card-rewards-icon-border-color,
      ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 65)}
    );
  color: var(--dcm-wallet-card-rewards-icon-color, ${({ theme }) => theme.customized.colors.buttonsLinks});
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

export const RewardsText = styled(Typography.Body)`
  ${brandColorReadable}
  flex-grow: 1;
`;

export const RewardsLink = styled.span`
  ${brandColorReadable}
  flex-shrink: 0;
  font-size: 14px;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: none;
  }
`;
